import * as React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { IReplaceMenuProps } from './replace-menu-types';
import { ReplaceMenuItem } from './replace-menu-item';
import { BackButton } from './parts/back-button';
import { IMenuItem, getCommonMenuStyle } from '../common';
import { useHierarchicalData } from '../../../hooks';
import { FlexBox } from '../../flex-box';

const slideIn = keyframes`
    from { transform: translateX(100%); }
    to { transform: translateX(0%); }
`;

const slideOut = keyframes`
    from { transform: translateX(-100%); }
    to { transform: translateX(0%); }
`;

const MenuAnimation = styled(FlexBox).attrs((props: { navigateToParent?: boolean }) => props)`
   ${({ navigateToParent }) => css`animation: ${navigateToParent ? slideOut : slideIn} 0.5s ease;`};
`;

const ReplaceMenuBase = React.memo((props: IReplaceMenuProps) => {
    const {
        className, backButtonText = 'Back', items, defaultSelectedId, selectedId,
        highlightSelectionPath, triggerChangeOnItemWithChildren, onChange, cursorType
    } = props;
    const isBackButtonClicked = React.useRef(false);
    const [currentItemIdInView, setCurrentItemIdInView] = React.useState<string>();
    const { hasChildren, getChildItemsByParentId, shouldHighlightItem, setSelectedId, getItemPathById } = useHierarchicalData({
        items,
        defaultSelectedId,
        selectedId
    });

    const initialItemsToRender = React.useMemo(() => {
        const initialSelectedItemId = selectedId || defaultSelectedId;
        if (initialSelectedItemId === undefined) {
            return getChildItemsByParentId();
        }
        const selectedItem = items.find(i => i.id === initialSelectedItemId);
        if (selectedItem) {
            setCurrentItemIdInView(selectedItem.parentId);
            return getChildItemsByParentId(selectedItem.parentId);
        }
        return getChildItemsByParentId();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [itemsToDisplay, setItemsToDisplay] = React.useState<IMenuItem[]>(initialItemsToRender);

    const _onNavigateBackToParent = React.useCallback(() => {
        isBackButtonClicked.current = true;
        const path = getItemPathById(currentItemIdInView);
        const parentId = path.length > 1 ? path[path.length - 2] : undefined;
        setCurrentItemIdInView(parentId as string);
        setItemsToDisplay(getChildItemsByParentId(parentId));
    }, [currentItemIdInView, getChildItemsByParentId, getItemPathById]);

    const _onSelect = React.useCallback((id: string) => {
        const doesHaveChildren = hasChildren(id);
        if (doesHaveChildren) {
            isBackButtonClicked.current = false;
            setCurrentItemIdInView(id);
            setItemsToDisplay(getChildItemsByParentId(id));
        }
        if (!triggerChangeOnItemWithChildren && doesHaveChildren) {
            return;
        }
        setSelectedId(id);
        if (onChange === undefined) {
            return;
        }
        onChange(id);
    }, [hasChildren, triggerChangeOnItemWithChildren, setSelectedId, onChange, getChildItemsByParentId]);

    const _onRenderItem = React.useCallback((item: IMenuItem) => {
        const { id } = item;
        return (
            <ReplaceMenuItem
                key={id}
                cursorType={cursorType}
                itemInfo={item}
                menuType="replaceMenu"
                isItemSelected={shouldHighlightItem(id)}
                hasChildren={hasChildren(id)}
                highlightSelectionPath={highlightSelectionPath}
                triggerChangeOnItemWithChildren={triggerChangeOnItemWithChildren}
                onSelect={_onSelect} />
        );
    }, [_onSelect, cursorType, hasChildren, highlightSelectionPath, shouldHighlightItem, triggerChangeOnItemWithChildren]);

    return (
        <div className={className}>
            <MenuAnimation
                flexDirection="column"
                navigateToParent={isBackButtonClicked.current}>
                {currentItemIdInView && hasChildren(currentItemIdInView) && <BackButton onNavigateBackToParent={_onNavigateBackToParent} text={backButtonText} />}
                {itemsToDisplay.map(_onRenderItem)}
            </MenuAnimation>
        </div>
    );
});

ReplaceMenuBase.displayName = 'ReplaceMenuBase';

export const ReplaceMenu = styled(ReplaceMenuBase).attrs({
    className: String('ap-replaceMenu')
})`
    display: inline-block;
    ${getCommonMenuStyle('replaceMenu')}
`;

ReplaceMenu.displayName = 'ReplaceMenu';
